import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { useEffect } from "react";
import { GlobalSearch } from "@/components/ui/global-search";
import { DashboardHeader } from "@/components/layout/dashboard-header";
import {
  LayoutDashboard,
  Activity,
  FlaskConical,
  Pill,
  ArrowLeftRight,
  Zap,
  BookOpen,
  MessageSquare,
  LogOut,
  Settings,
  ClipboardList,
  Scale,
  ShieldCheck,
  GraduationCap,
  TrendingUp,
  Shield,
  Star,
  FileSearch,
  AlertTriangle,
  Users,
  PillBottle,
  CalendarDays,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { HcpVerificationBanner } from "@/components/ui/hcp-verification-banner";
import { LegalFooter } from "@/components/layout/legal-footer";

export function PharmacistLayout({ children, title }: { children: React.ReactNode; title: string }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | RheumHub`;
    return () => { document.title = "RheumHub"; };
  }, [title]);

  const clinicalItems = [
    { href: "/pharmacist", label: "Dashboard", icon: LayoutDashboard },
    { href: "/pharmacist/monitoring", label: "DMARD & Biologic Monitoring", icon: FlaskConical },
    { href: "/pharmacist/dose-optimisation", label: "Dose Optimisation", icon: Scale },
    { href: "/pharmacist/biosimilars", label: "Biosimilar Guide", icon: ArrowLeftRight },
    { href: "/pharmacist/interactions", label: "Drug Interactions", icon: Zap },
    { href: "/pharmacist/smpc", label: "SmPC Reference", icon: FileSearch },
  ];

  const patientCareItems = [
    { href: "/pharmacist/counselling", label: "Patient Counselling", icon: ClipboardList },
  ];

  const professionalItems = [
    { href: "/pharmacist/nmp", label: "Non-Medical Prescribing", icon: Pill },
    { href: "/pharmacist/chat", label: "AI Medicines Adviser", icon: MessageSquare },
  ];

  const cpdItems = [
    { href: "/pharmacist/learn", label: "Learning Hub", icon: BookOpen },
    { href: "/pharmacist/cpd", label: "CPD Activity Log", icon: BookOpen },
    { href: "/pharmacist/cpd/revalidation", label: "GPhC Revalidation", icon: Shield },
    { href: "/pharmacist/cpd/rps-faculty", label: "RPS Faculty Portfolio", icon: GraduationCap },
    { href: "/pharmacist/cpd/prescribing-competency", label: "Prescribing Competency", icon: Star },
    { href: "/pharmacist/cpd/cppe", label: "CPPE Learning", icon: BookOpen },
  ];

  const careerItems = [
    { href: "/pharmacist/career", label: "Career Pathways", icon: TrendingUp },
    { href: "/pharmacist/career/rps-membership", label: "RPS Membership & Fellowship", icon: GraduationCap },
  ];

  const eventsItems = [
    { href: "/pharmacist/events", label: "Events", icon: CalendarDays },
  ];

  const governanceItems = [
    { href: "/hcp/safety-report", label: "Report Safety Concern", icon: AlertTriangle },
    { href: "/pharmacist/settings", label: "Settings", icon: Settings },
  ];

  const isActive = (href: string) =>
    location === href || (location.startsWith(href + "/") && href !== "/pharmacist");

  const isActiveExact = (href: string) => location === href;

  const renderNavItem = (item: { href: string; label: string; icon: React.ElementType }, exact = false) => {
    const active = exact ? isActiveExact(item.href) : isActive(item.href);
    return (
      <Link
        key={item.href}
        href={item.href}
        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
          active
            ? "bg-[#6ee7a8]/15 text-[#6ee7a8]"
            : "hover:bg-white/10 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-[#6ee7a8]" : "text-white/35"}`} />
        <span className="leading-tight">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SessionNoticeBanner />
      <LastSignInBanner />
      <HcpVerificationBanner />
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 bg-[#0f3d22] text-white/75 flex flex-col fixed inset-y-0 z-20">
          <div className="p-6 flex items-center gap-3 text-white">
            <Activity className="h-6 w-6 text-[#6ee7a8]" />
            <span className="text-xl font-bold tracking-tight">RheumHub</span>
          </div>

          <div className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-[#6ee7a8]/60">
            Pharmacist Portal
          </div>

          <nav className="sidebar-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <div className="pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Clinical Tools
              </div>
            </div>
            {clinicalItems.map((item) => renderNavItem(item, item.href === "/pharmacist"))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Patient Care
              </div>
            </div>
            {patientCareItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Professional
              </div>
            </div>
            {professionalItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                CPD Portfolio
              </div>
            </div>
            {cpdItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Career
              </div>
            </div>
            {careerItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Events
              </div>
            </div>
            {eventsItems.map((item) => renderNavItem(item))}

            <div className="pt-2.5 pb-0.5">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20 mb-0.5">
                Governance
              </div>
            </div>
            {governanceItems.map((item) => renderNavItem(item))}
          </nav>

          <div className="p-3 border-t border-white/10 space-y-0.5">
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "bg-[#7bb8ff]/15 text-[#7bb8ff]"
                    : "hover:bg-white/10 hover:text-white text-white/35"
                }`}
              >
                <Users className="h-4 w-4 shrink-0" />
                Admin Panel
              </Link>
            )}
            <button
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium hover:bg-white/10 transition-colors text-white/35 hover:text-white"
              data-testid="nav-signout"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 pl-64 flex flex-col min-h-screen">
          <DashboardHeader title={title} profileLink="/pharmacist/profile" />
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© RheumHub · For UK registered pharmacists only · AI responses do not constitute clinical advice · Grounded in GPhC, RPS, BNF, NHSE"
            extraLinks={[{ label: "Clinical Safety", href: "/clinical-safety" }]}
          />
        </main>
      </div>
    </div>
  );
}
